/**
 * API Base de datos: listas, contactos, campañas, métricas (CRUD).
 * Conexión Mailup: ver servicios/mailup (oauth, sync) o llamar a /api/mailup.
 */

const BASE = process.env.REACT_APP_API_URL || '/api';

function fetchApi(path: string, options?: RequestInit) {
  return fetch(`${BASE}${path}`, { credentials: 'include', ...options });
}

/** Parsea respuesta como JSON; si el cuerpo no es JSON, devuelve el texto en el error. */
async function parseResponse(res: Response): Promise<{ success: boolean; data?: unknown; error?: string; message?: string }> {
  const text = await res.text();
  let body: Record<string, unknown>;
  try {
    body = text ? JSON.parse(text) : {};
  } catch {
    const preview = text.slice(0, 80);
    throw new Error(!res.ok ? `Error ${res.status}: ${preview}${text.length > 80 ? '...' : ''}` : `Respuesta no es JSON: ${preview}`);
  }
  if (!res.ok) throw new Error((body.error as string) || (body.message as string) || `Error ${res.status}`);
  return body as { success: boolean; data?: unknown; error?: string; message?: string };
}

async function json<T>(res: Response): Promise<{ success: boolean; data?: T; error?: string }> {
  const body = await parseResponse(res);
  return body as { success: boolean; data?: T; error?: string };
}

// Listas
export async function getLists() {
  const res = await fetchApi('/database/lists');
  const b = await json<any[]>(res);
  return b.data ?? [];
}

export async function getList(id: number) {
  const res = await fetchApi(`/database/lists/${id}`);
  return (await json(res)).data;
}

export async function createList(body: { name?: string; description?: string; external_id?: number }) {
  const res = await fetchApi('/database/lists', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return (await json(res)).data;
}

export async function updateList(id: number, body: { name?: string; description?: string; external_id?: number }) {
  const res = await fetchApi(`/database/lists/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return (await json(res)).data;
}

export async function deleteList(id: number) {
  const res = await fetchApi(`/database/lists/${id}`, { method: 'DELETE' });
  await json(res);
}

// Contactos
export async function getListContacts(listId: number) {
  const res = await fetchApi(`/database/lists/${listId}/contacts`);
  const b = await json<any[]>(res);
  return b.data ?? [];
}

export async function createListContact(listId: number, body: { email: string; name?: string; status?: string }) {
  const res = await fetchApi(`/database/lists/${listId}/contacts`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return (await json(res)).data;
}

export async function updateListContact(listId: number, id: number, body: { email?: string; name?: string; status?: string }) {
  const res = await fetchApi(`/database/lists/${listId}/contacts/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return (await json(res)).data;
}

export async function deleteListContact(listId: number, id: number) {
  const res = await fetchApi(`/database/lists/${listId}/contacts/${id}`, { method: 'DELETE' });
  await json(res);
}

// Campañas
export async function getCampaigns(listId?: number) {
  const q = listId != null ? `?listId=${listId}` : '';
  const res = await fetchApi(`/database/campaigns${q}`);
  const b = await json<any[]>(res);
  return b.data ?? [];
}

export async function getCampaign(id: number) {
  const res = await fetchApi(`/database/campaigns/${id}`);
  return (await json(res)).data;
}

export async function createCampaign(body: { subject?: string; sent_at?: string; list_id?: number; external_message_id?: number }) {
  const res = await fetchApi('/database/campaigns', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return (await json(res)).data;
}

export async function updateCampaign(id: number, body: { subject?: string; sent_at?: string; list_id?: number; external_message_id?: number }) {
  const res = await fetchApi(`/database/campaigns/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return (await json(res)).data;
}

export async function deleteCampaign(id: number) {
  const res = await fetchApi(`/database/campaigns/${id}`, { method: 'DELETE' });
  await json(res);
}

// Métricas (solo lectura)
export async function getCampaignMetrics(campaignId: number) {
  const res = await fetchApi(`/database/campaigns/${campaignId}/metrics`);
  const b = await json<any>(res);
  return b.data ?? null;
}

// Mailup (conexión y sincronización)
export async function getMailupStatus(): Promise<{ connected: boolean }> {
  const res = await fetchApi('/mailup/status');
  const b = await json<{ connected: boolean }>(res);
  return { connected: !!(b as any).connected };
}

export function getMailupOAuthUrl(): string {
  return `${BASE}/mailup/oauth`;
}

export async function mailupSyncLists(): Promise<{ success: boolean; synced?: number; listIds?: number[]; error?: string }> {
  const res = await fetchApi('/mailup/sync/lists', { method: 'POST' });
  const b = await parseResponse(res);
  return b as { success: boolean; synced?: number; listIds?: number[] };
}

export async function mailupSyncListContacts(mailupListId: number) {
  const res = await fetchApi(`/mailup/sync/lists/${mailupListId}/contacts`, { method: 'POST' });
  return json(res);
}

export async function mailupSyncMetrics(listId?: number) {
  const q = listId != null ? `?listId=${listId}` : '';
  const res = await fetchApi(`/mailup/sync/metrics${q}`, { method: 'POST' });
  return json(res);
}

export async function mailupSyncAll(): Promise<{ success: boolean; lists?: number; metrics?: unknown; error?: string }> {
  const res = await fetchApi('/mailup/sync/all', { method: 'POST' });
  const b = await parseResponse(res);
  return b as { success: boolean; lists?: number; metrics?: unknown };
}

// ——— Organizaciones (BD propia, identificador = domain) ———
export async function getOrganizations() {
  const res = await fetchApi('/database/organizations');
  const b = await json<any[]>(res);
  return b.data ?? [];
}

export async function getOrganization(id: number) {
  const res = await fetchApi(`/database/organizations/${id}`);
  return (await json(res)).data;
}

export async function createOrganization(body: { domain?: string; name?: string; description?: string }) {
  const res = await fetchApi('/database/organizations', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return (await json(res)).data;
}

export async function updateOrganization(id: number, body: { domain?: string; name?: string; description?: string }) {
  const res = await fetchApi(`/database/organizations/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return (await json(res)).data;
}

export async function deleteOrganization(id: number) {
  const res = await fetchApi(`/database/organizations/${id}`, { method: 'DELETE' });
  await json(res);
}

// ——— Contactos (BD propia, identificador = email; vínculo org por domain) ———
export async function getContacts(organizationId?: number) {
  const q = organizationId != null ? `?organizationId=${organizationId}` : '';
  const res = await fetchApi(`/database/contacts${q}`);
  const b = await json<any[]>(res);
  return b.data ?? [];
}

export async function getContact(id: number) {
  const res = await fetchApi(`/database/contacts/${id}`);
  return (await json(res)).data;
}

export async function createContact(body: { email: string; domain?: string; name?: string; organization_id?: number }) {
  const res = await fetchApi('/database/contacts', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return (await json(res)).data;
}

export async function updateContact(id: number, body: { email?: string; domain?: string; name?: string; organization_id?: number }) {
  const res = await fetchApi(`/database/contacts/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return (await json(res)).data;
}

export async function deleteContact(id: number) {
  const res = await fetchApi(`/database/contacts/${id}`, { method: 'DELETE' });
  await json(res);
}

export async function importOrganizations(payload: { columns: string[]; rows: Record<string, unknown>[] }) {
  const res = await fetchApi('/database/import/organizations', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return parseResponse(res) as Promise<{ success: boolean; inserted?: number; updated?: number; error?: string }>;
}

export async function importContacts(payload: { columns: string[]; rows: Record<string, unknown>[] }) {
  const res = await fetchApi('/database/import/contacts', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return parseResponse(res) as Promise<{ success: boolean; inserted?: number; updated?: number; skipped?: number; error?: string }>;
}

export type WebParticipantsImportStats = {
  credenciales_web: number;
  networking_web: number;
  inscripciones_generales: number;
  total: number;
};

export async function getWebParticipantsImportStats() {
  const res = await fetchApi('/database/import/web-participants/stats');
  const b = await json<WebParticipantsImportStats>(res);
  return b.data ?? {
    credenciales_web: 0,
    networking_web: 0,
    inscripciones_generales: 0,
    total: 0,
  };
}

export async function importCredencialesWeb(payload: {
  columns: string[];
  rows: Record<string, unknown>[];
  sourceFile?: string;
}) {
  const res = await fetchApi('/database/import/credenciales-web', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return parseResponse(res) as Promise<{
    success: boolean;
    inserted?: number;
    updated?: number;
    skipped?: number;
    stats?: WebParticipantsImportStats;
    error?: string;
  }>;
}

export async function importNetworkingWeb(payload: {
  columns: string[];
  rows: Record<string, unknown>[];
  sourceFile?: string;
}) {
  const res = await fetchApi('/database/import/networking-web', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return parseResponse(res) as Promise<{
    success: boolean;
    inserted?: number;
    updated?: number;
    skipped?: number;
    stats?: WebParticipantsImportStats;
    error?: string;
  }>;
}

export async function importInscripcionesGenerales(payload: {
  columns: string[];
  rows: Record<string, unknown>[];
  sourceFile?: string;
}) {
  const res = await fetchApi('/database/import/inscripciones-generales', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return parseResponse(res) as Promise<{
    success: boolean;
    inserted?: number;
    updated?: number;
    skipped?: number;
    stats?: WebParticipantsImportStats;
    error?: string;
  }>;
}

